import type { HxIconCategory, HxIconPickerIcon } from './icon-picker.models';

interface IconManifest {
  icons: HxIconPickerIcon[];
}

/**
 * Loads all icon categories from the manifest.json file. Used by components that need the full icon library (e.g., text
 * editor).
 *
 * @returns Promise resolving to array of icon categories
 */
export async function loadIconCategories(): Promise<HxIconCategory[]> {
  try {
    const response = await fetch('/assets/icons/manifest.json');
    if (!response.ok) {
      console.warn('Failed to load icon manifest:', response.statusText);
      return [];
    }

    const manifest = (await response.json()) as IconManifest;
    return groupIconsByCategory(manifest.icons);
  } catch (error) {
    console.error('Failed to load icon manifest', error);
    return [];
  }
}

function groupIconsByCategory(icons: HxIconPickerIcon[]): HxIconCategory[] {
  const groups = new Map<
    string,
    {
      icons: HxIconPickerIcon[];
      order: number;
    }
  >();

  icons.forEach((icon) => {
    const name = getCategoryName(icon);
    const order = typeof icon.categoryOrder === 'number' ? icon.categoryOrder : Number.MAX_SAFE_INTEGER;
    if (!groups.has(name)) {
      groups.set(name, { icons: [], order });
    }
    const group = groups.get(name)!;
    if (!group.icons.some((existing) => existing.name === icon.name)) {
      group.icons.push(icon);
    }
  });

  return Array.from(groups.entries())
    .map(([name, entry]) => ({
      name,
      icons: entry.icons.slice().sort(compareIconOrder),
      order: entry.order,
    }))
    .sort((a, b) => compareCategoryOrder(a, b))
    .map(({ name, icons }) => ({ name, icons }));
}

function getCategoryName(icon: HxIconPickerIcon): string {
  if (icon.categoryName) {
    return icon.categoryName;
  }

  const prefix = icon.name.split('-')[0] || 'Other';
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

function compareIconOrder(a: HxIconPickerIcon, b: HxIconPickerIcon): number {
  const orderA = typeof a.iconOrder === 'number' ? a.iconOrder : Number.MAX_SAFE_INTEGER;
  const orderB = typeof b.iconOrder === 'number' ? b.iconOrder : Number.MAX_SAFE_INTEGER;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  return a.name.localeCompare(b.name);
}

function compareCategoryOrder(a: { name: string; order: number }, b: { name: string; order: number }): number {
  if (a.order !== b.order) {
    return a.order - b.order;
  }

  return a.name.localeCompare(b.name);
}
